import { Receipt, Expense, Income, CreditNote } from "../classes";
import Helper from "../plugins/Helper/Helper";
import i18n from '@/plugins/vue-i18n';
import Vue from "vue";

export class IncomeExpense {
  constructor(data) {
    this.date = data.date
    this.documentId = data.documentId || ""
    this.detail = data.detail || ""
    this.contractId = data.contractId || ""
    this.category = data.category || ""
    this.categoryItem = data.categoryItem || ""
    this.totalNonVatValue = data.totalNonVatValue || 0
    this.totalVatValue = data.totalVatValue || 0
    this.totalValue = data.totalValue || 0
    this.createdBy = data.createdBy
    this.type = data.type
    this.receipt = data.receipt
    this.expense = data.expense
    this.income = data.income
    this.creditNote = data.creditNote
    this.createdByText = this.createdBy ? `${this.createdBy.firstName} ${this.createdBy.lastName}` : '-'
    this.id = data.id
    this.currencyCode = data.currencyCode || Vue.prototype.$globalCurrencyCode || "THB"
  }

  static async getIncomeExpenseBetweenDateByMarket(fromData, callBack) {
    let incomeExpenseList = []
    let receiptSelect = [
      "date",
      "isPaid",
      "documentId",
      "contract.contractId",
      "contract.businessName",
      "booking.businessName",
      "invoice.documentId",
      "documentType",
      "totalNonVatValue",
      "totalVatValue",
      "totalValue",
      "createdBy.firstName",
      "createdBy.lastName",
      "currencyCode",
      "exchangeRate",
      "paymentCurrencyCode",
      "currencyRate",
      "totalPaymentValue"
    ]
    let incomeSelect = [
      "date",
      "detail",
      "contract.contractId",
      "category.name",
      "category.isRemoved",
      "originalPrice",
      "totalVatValue",
      "totalValue",
      "createdBy.firstName",
      "createdBy.lastName",
      "currencyCode"
    ]
    fromData.isDesc = true

    let [receipts, expenses, incomes, creditNotes] = await Promise.all([
      Helper.queryMore(Receipt.getReceiptsBetweenDateByMarket, {...fromData, isRaw: true, select: receiptSelect, isToMainCurrency: true}, null,(_receipts) => {
        let _incomeExpenseList = []
        for (let receipt of _receipts) {
          if (receipt.documentId && receipt.isPaid) {
            _incomeExpenseList.push(IncomeExpense.generateByReceipt(receipt))
          }
        }
        if (callBack) callBack(_incomeExpenseList)
      }),
      Helper.queryMore(Expense.getExpenseBetweenDateByMarket, {...fromData, select: incomeSelect}, null, (_expenses) => {
        let _incomeExpenseList = []
        for (let expense of _expenses) {
          _incomeExpenseList.push(IncomeExpense.generateByExpense(expense))
        }
        if (callBack) callBack(_incomeExpenseList)
      }),
      Helper.queryMore(Income.getIncomeBetweenDateByMarket, {...fromData, select: incomeSelect}, null, (_incomes) => {
        let _incomeExpenseList = []
        for (let income of _incomes) {
          _incomeExpenseList.push(IncomeExpense.generateByIncome(income))
        }
        if (callBack) callBack(_incomeExpenseList)
      }),
      Helper.queryMore(CreditNote.getCreditNotesBetweenDateByMarket, {...fromData, isRaw: true, select: receiptSelect}, null, (_creditNotes) => {
        let _incomeExpenseList = []
        for (let creditNote of _creditNotes) {
          if (creditNote.documentId && creditNote.isPaid) {
            _incomeExpenseList.push(IncomeExpense.generateByCreditNote(creditNote))
          }
        }
        if (callBack) callBack(_incomeExpenseList)
      })
    ])

    for (let receipt of receipts) {
      if (receipt.documentId && receipt.isPaid) {
        incomeExpenseList.push(IncomeExpense.generateByReceipt(receipt))
      }
    }

    for (let expense of expenses) {
      incomeExpenseList.push(IncomeExpense.generateByExpense(expense))
    }

    for (let income of incomes) {
      incomeExpenseList.push(IncomeExpense.generateByIncome(income))
    }

    for (let creditNote of creditNotes) {
      if (creditNote.documentId) {
        incomeExpenseList.push(IncomeExpense.generateByCreditNote(creditNote))
      }
    }

    return incomeExpenseList.sort(function (a, b) {
      return new Date(b.date) - new Date(a.date)
    })
  }

  static generateByReceipt(receipt) {
    let businessName = ""
    let documentTypeString = ""
    if (receipt.contract) businessName = receipt.contract.businessName || ""
    if (receipt.booking) businessName = receipt.booking.businessName || ""
    if (receipt.documentType) documentTypeString = Receipt.getDocumentTypeString(receipt.documentType)
    let income = new IncomeExpense({
      id: receipt.objectId,
      date: receipt.date,
      documentId: receipt.documentId || "-",
      detail: `${i18n.t('payment.receive_money')} ${documentTypeString} ${i18n.t('from')} ${businessName}`,
      contractId: receipt.contract?.contractId || "-",
      category: documentTypeString,
      totalNonVatValue: receipt.totalNonVatValue,
      totalVatValue: receipt.totalVatValue,
      totalValue: receipt.totalValue,
      createdBy: receipt.createdBy,
      type: "receipt",
      currencyCode: receipt.currencyCode,
      receipt: receipt
    })
    return income
  }

  static generateByExpense(expense) {
    let _expense = new IncomeExpense({
      id: expense.objectId,
      date: expense.date.iso,
      detail: expense.detail || "-",
      documentId: "-",
      contractId: expense.contract?.contractId || "-",
      category: expense.category.name || "-",
      categoryItem: expense.category,
      totalNonVatValue: expense.originalPrice * -1,
      totalVatValue: expense.totalVatValue * -1,
      totalValue: expense.totalValue * -1,
      createdBy: expense.createdBy,
      type: "expense",
      currencyCode: expense.currencyCode,
      expense: expense
    })
    return _expense
  }

  static generateByIncome(income) {
    let _income = new IncomeExpense({
      id: income.objectId,
      date: income.date.iso,
      detail: income.detail || "-",
      documentId: "-",
      contractId: income.contract?.contractId || "-",
      category: income.category.name || "-",
      categoryItem: income.category,
      totalNonVatValue: income.originalPrice,
      totalVatValue: income.totalVatValue,
      totalValue: income.totalValue,
      createdBy: income.createdBy,
      type: "income",
      currencyCode: income.currencyCode,
      income: income
    })
    return _income
  }

  static generateByCreditNote(creditNote) {
    let expense = new IncomeExpense({
      id: creditNote.objectId,
      date: creditNote.date.iso,
      documentId: creditNote.documentId || "-",
      detail: `${i18n.t('credit_note.reduce_debt_to')} ${creditNote.invoice?.documentId}`,
      contractId: creditNote.contract?.contractId || "-",
      category: i18n.t('credit_note.title'),
      totalNonVatValue: creditNote.totalNonVatValue * -1,
      totalVatValue: creditNote.totalVatValue * -1,
      totalValue: creditNote.totalValue * -1,
      createdBy: creditNote.createdBy,
      type: "credit-note",
      currencyCode: creditNote.currencyCode,
      creditNote: creditNote
    })
    return expense
  }
}